import { HttpStatusCode } from '@angular/common/http';
import { ADYEN_ERROR } from '@constants/adyen-error.constants';
import { AdyenBookingRefusal } from '@models/booking-outcome.model';

type KeyedError = { status?: number; error?: unknown } | null | undefined;

/**
 * The translation key cakephp sent with a refusal (`{"errors":[{"message","code","key","params"}]}`),
 * or undefined when there is none - every older answer, and a 200 re-thrown by the interceptor,
 * whose payload is an `Error` rather than the body.
 */
export function adyenErrorKeyOf(error: KeyedError): string | undefined {
  const body = error?.error as { errors?: { key?: unknown }[] } | undefined;
  const key = Array.isArray(body?.errors) ? body.errors[0]?.key : undefined;

  return typeof key === 'string' ? key : undefined;
}

/**
 * The message the payment pane shows for a failed `POST adyen/sessions`, or undefined for an unknown
 * key - the caller then keeps its status-based text.
 *
 * `payment_unavailable` carries its own retry verdict in the status: 503 (Adyen unreachable, rate
 * limited) clears on its own and gets the retryable "temporarily" text; 400 (credentials or request
 * refused) will not, and gets the final "not available" text.
 */
export function adyenSessionErrorMessageKey(error: KeyedError): string | undefined {
  switch (adyenErrorKeyOf(error)) {
    case ADYEN_ERROR.KEYS.PAYMENT_UNAVAILABLE:
      return (error?.status ?? 0) >= HttpStatusCode.InternalServerError
        ? ADYEN_ERROR.PANE_MESSAGES.TEMPORARILY_UNAVAILABLE
        : ADYEN_ERROR.PANE_MESSAGES.UNAVAILABLE;
    case ADYEN_ERROR.KEYS.NOT_ENABLED_FOR_BOOKING:
      return ADYEN_ERROR.PANE_MESSAGES.UNAVAILABLE;
    case ADYEN_ERROR.KEYS.RESCHEDULE_NOT_ALLOWED:
      return ADYEN_ERROR.PANE_MESSAGES.RESCHEDULE_NOT_ALLOWED;
    default:
      return undefined;
  }
}

/**
 * What a keyed refusal of `/book` means for the summary, or undefined for an unknown key - the
 * caller then keeps its status-based handling and the backend's own message.
 *
 * - `booking_refused`: the card payment was not accepted, nothing was booked. The customer may pay
 *   again on a new session.
 * - `session_already_used`: the backend will not claim this payment session again. A booking may or
 *   may not stand behind it, so the customer checks their email before re-arming the payment.
 * - `session_expired`: the payment's session expired before it was booked. Nothing was booked and
 *   cakephp refunds any amount taken, so - as after a declined card - a new session can be paid.
 */
export function adyenBookingRefusal(error: KeyedError): AdyenBookingRefusal | undefined {
  switch (adyenErrorKeyOf(error)) {
    case ADYEN_ERROR.KEYS.BOOKING_REFUSED:
      return { failure: 'declined', messageKey: ADYEN_ERROR.BOOKING_MESSAGES.BOOKING_REFUSED, courtesy: true };
    case ADYEN_ERROR.KEYS.SESSION_ALREADY_USED:
      return { failure: 'spent', messageKey: ADYEN_ERROR.BOOKING_MESSAGES.SESSION_SPENT, courtesy: true };
    case ADYEN_ERROR.KEYS.SESSION_EXPIRED:
      return { failure: 'declined', messageKey: ADYEN_ERROR.BOOKING_MESSAGES.SESSION_EXPIRED, courtesy: true };
    case ADYEN_ERROR.KEYS.PAYMENT_UNAVAILABLE:
    case ADYEN_ERROR.KEYS.NOT_ENABLED_FOR_BOOKING:
      return { messageKey: ADYEN_ERROR.PANE_MESSAGES.UNAVAILABLE, courtesy: false };
    case ADYEN_ERROR.KEYS.RESCHEDULE_NOT_ALLOWED:
      return { messageKey: ADYEN_ERROR.PANE_MESSAGES.RESCHEDULE_NOT_ALLOWED, courtesy: false };
    default:
      return undefined;
  }
}

/**
 * True when cakephp refused a `/book` because its payment session already backs a booking of this
 * partner (`params.already_booked = 1` beside `session_already_used`) - a replayed /book, or two tabs
 * booking one payment. That booking exists; nothing may be paid or booked again for it.
 */
export function isAdyenAlreadyBooked(error: KeyedError): boolean {
  if (adyenErrorKeyOf(error) !== ADYEN_ERROR.KEYS.SESSION_ALREADY_USED) {
    return false;
  }
  const body = error?.error as { errors?: { params?: { already_booked?: unknown } }[] } | undefined;

  return Number(body?.errors?.[0]?.params?.already_booked) === 1;
}
